<template>
  <div :class="$style.categoryPage">
    <aw-breadcrumb-version2 has-tooltip :class="$style.categoryPageBreadcrumbs" />

    <aw-cms-content-list
      :content-position="CCP_ABOVE_FIRST_ROW"
      :content-list="contentData"
      :content-list-lengths="contentDataLengths"
      :class="$style.categoryPageCms"
    />

    <div v-if="category" :class="$style.categoryPageCategories">
      <lv-heading
        :ref="Navigation[ID_CATEGORY_TOP]"
        tag="h1"
        level="2"
        version2
        :class="$style.categoryPageCategoriesHead"
      >
        {{ getCategoryDisplayName(category) }} {{ $awt('aw.category.title') }}
      </lv-heading>
      <aw-category-list :category="category" :class="$style.categoryPageCategoriesList" />
      <aw-separator :class="$style.categoryPageCategoriesSeparator" />
    </div>

    <div>
      <template v-for="(childCategory, index) in category?.children?.slice(0, 3) || []" :key="childCategory.id">
        <aw-cms-content-list
          v-if="hasContentByPosition(contentPageTypePositionsByIndex[index + 1])"
          :content-position="contentPageTypePositionsByIndex[index + 1]"
          :content-list="contentData"
          :content-list-lengths="contentDataLengths"
          :class="$style.categoryPageCms"
        />

        <div :class="$style.categoryListWrapper">
          <aw-category-preview-carousel
            :category="childCategory"
          />
        </div>
      </template>
    </div>

    <aw-cms-content-list
      :content-position="CCP_UNDER_PRODUCT_LIST"
      :content-list="contentData"
      :content-list-lengths="contentDataLengths"
      :class="$style.categoryPageCms"
    />

    <div :class="$style.categoryPageStaticContent">
      <div>
        <lv-heading
          tag="h2"
          level="3"
          version2
          :class="$style.categoryPageStaticContentContactUsTitle"
        >
          {{ $awt('aw.cms.contact_us.title') }}
        </lv-heading>
        <aw-cms-contact-us />
      </div>
      <aw-ok-auchan-mobile-app />
    </div>

    <aw-content-seo-text
      v-if="isShop && category?.seoTextId"
      :content-id="category.seoTextId"
    />
  </div>
</template>

<script setup>
  import { computed, defineAsyncComponent, onMounted, watch } from 'vue';
  import { useAsyncData } from 'nuxt/app';
  import { contentPageTypePositionsByIndex, getContentDataLengths, getSortedContentDataByPos } from '~~/common/utils/contentData';
  import { getCategoryDisplayName } from '~~/common/utils/category';
  import { useContentsStore } from '~~/common/stores/contents';
  import { ID_CATEGORY_TOP, Navigation, useFocusRef } from '~~/common/config/Navigation';
  import { LvHeading } from '~~/common/components/loginet-vue-shop/index';
  import AwBreadcrumbVersion2 from '~~/common/components/Common/AwBreadcrumbVersion2';
  import AwCategoryList from '~~/common/components/Page/Category/AwCategoryList';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import AwCmsContactUs from '~~/common/components/AwCmsContactUs';
  import AwOkAuchanMobileApp from '~~/common/components/AwOkAuchanMobileApp';
  import AwCategoryPreviewCarousel from '~~/common/components/Page/Category/AwCategoryPreviewCarousel.vue';
  import AwCmsContentList from '~~/common/components/Page/Content/AwCmsContentList.vue';

  import { CCP_ABOVE_FIRST_ROW, CCP_UNDER_PRODUCT_LIST } from '~~/common/config/ContentPageTypePosition';

  const AwContentSeoText = defineAsyncComponent(() => import('~~/shop/app/components/AwContentSeoText'));

  const props = defineProps({
    category: {
      required: true,
      type: Object,
    },
  });

  const contentsStore = useContentsStore();

  const isShop = __AW_IS_SHOP__;
  const focusRef = useFocusRef();

  const contentData = computed(() => {
    return getSortedContentDataByPos(contentsStore.dataByResourceId, contentPageTypePositionsByIndex);
  });
  const contentDataLengths = computed(() => {
    return getContentDataLengths(contentData.value);
  });
  onMounted(() => {
    if (props.category) {
      focusRef({ refId: Navigation[ID_CATEGORY_TOP] });
    } else {
      watch(
        () => props.category,
        () => {
          focusRef({ refId: Navigation[ID_CATEGORY_TOP] });
        },
        {
          once: true,
        },
      );
    }
  });
  await useAsyncData(`product_list_content${props.category?.id}`, async () => {
    await contentsStore.fetchStaticContentByResourceId({
      type: 'product_list_content',
      resourceId: props.category?.id,
    });
    return {};
  });

  function hasContentByPosition (position) {
    return contentData.value.some(content => content.position === position);
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.categoryListWrapper {
  position: relative;
  overflow: auto;
  scrollbar-width: none;
  padding-bottom: 40px;

  @include tablet(min) {
    padding-bottom: 80px;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.categoryPage {
  margin-bottom: 20px;
  @include tablet(min) {
    margin-bottom: 60px;
  }

  &Breadcrumbs {
    margin-bottom: 20px;
    @include tablet(min) {
      margin-bottom: 40px;
    }
  }

  &Categories {
    &Head {
      margin-right: 16px !important;
      margin-bottom: 20px !important;
      margin-left: 16px !important;
      @include tablet(min) {
        margin-bottom: 40px !important;
      }
    }

    &Separator {
      margin-bottom: 40px;
      @include tablet(min) {
        margin-bottom: 80px;
      }
    }
  }

  &Cms {
    margin-top: 20px;
    margin-bottom: 20px;
    @include tablet(min) {
      margin-top: 40px;
      margin-bottom: 40px;
    }
  }

  &StaticContent {
    display: flex;
    flex-direction: column;
    gap: 40px;

    @include tablet(min) {
      gap: 80px;
    }

    &ContactUs {
      &Title {
        margin-bottom: 20px !important;
        @include tablet(min) {
          margin-bottom: 40px !important;
        }
      }
    }
  }
}
</style>
